import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Chip,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import { useApiGet, getApiErrorMessage } from '../../utils/httpClientTs';
import {
  getScheduledTasks,
  queueFundStatementJobs,
  queueBackfillUserImisIDs,
  queueBackfillAddressGeocodes,
} from '../../endpoints/scheduledTasks';
import { ScheduledTask } from '../../types/scheduledTasks';
import AlertItem from '../../Components/AlertItem';
import dayjs from 'dayjs';

const ScheduledTasks: React.FC = () => {
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);
  const [asOfDate, setAsOfDate] = useState('');
  const [queueing, setQueueing] = useState(false);
  const [queueingImisBackfill, setQueueingImisBackfill] = useState(false);
  const [queueingGeocodeBackfill, setQueueingGeocodeBackfill] = useState(false);

  const {
    data: tasks,
    isLoading,
    error,
  } = useApiGet<ScheduledTask[]>(['scheduled-tasks'], getScheduledTasks, {
    enabled: true,
    retry: 1,
  });

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    try {
      return dayjs(dateString).format('MMM DD, YYYY hh:mm A');
    } catch {
      return 'Invalid date';
    }
  };

  const formatNextRun = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Not scheduled';
    try {
      return dayjs(dateString).format('MMM DD, YYYY hh:mm A');
    } catch {
      return 'Invalid date';
    }
  };

  const handleQueueFundStatementJobs = async () => {
    setQueueing(true);
    setAlertState(null);
    try {
      const resp = await queueFundStatementJobs(
        asOfDate.trim() !== '' ? asOfDate.trim() : undefined
      );
      const dateLabel = resp.as_of_date
        ? ` for as-of ${resp.as_of_date}`
        : ' for all MIP years';
      setAlertState({
        severity: 'success',
        title: 'Jobs queued',
        description: `Queued ${resp.queued.join(' → ')}${dateLabel}. The worker will fan out per-fund tasks; check worker logs or task logs for progress.`,
      });
    } catch (err) {
      setAlertState({
        severity: 'error',
        title: 'Failed to queue jobs',
        description:
          getApiErrorMessage(err) ||
          'Could not queue fund statement jobs. Try again or check Pub/Sub configuration.',
      });
    } finally {
      setQueueing(false);
    }
  };

  const handleQueueBackfillUserImisIDs = async () => {
    setQueueingImisBackfill(true);
    setAlertState(null);
    try {
      const resp = await queueBackfillUserImisIDs();
      setAlertState({
        severity: 'success',
        title: 'Job queued',
        description: `Queued ${resp.queued}. The worker will assign Member IDs (imis_id) to users missing one; check worker logs or task logs for progress.`,
      });
    } catch (err) {
      setAlertState({
        severity: 'error',
        title: 'Failed to queue job',
        description:
          getApiErrorMessage(err) ||
          'Could not queue imis_id backfill. Try again or check Pub/Sub configuration.',
      });
    } finally {
      setQueueingImisBackfill(false);
    }
  };

  const handleQueueBackfillAddressGeocodes = async () => {
    setQueueingGeocodeBackfill(true);
    setAlertState(null);
    try {
      const resp = await queueBackfillAddressGeocodes();
      setAlertState({
        severity: 'success',
        title: 'Job queued',
        description: `Queued ${resp.queued.join(', ')}. Worker geocodes living members' primary addresses and chapters (postal centroid or address); check worker/task logs for progress.`,
      });
    } catch (err) {
      setAlertState({
        severity: 'error',
        title: 'Failed to queue job',
        description:
          getApiErrorMessage(err) ||
          'Could not queue address geocode backfill. Try again or check Pub/Sub configuration.',
      });
    } finally {
      setQueueingGeocodeBackfill(false);
    }
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={2}>
        <Typography variant="h4" color="primary">
          Scheduled Tasks
        </Typography>
      </Grid>

      {alertState && (
        <Grid item xs={12}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}

      <Grid item xs={12} sx={{ px: 2, pb: 2 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Fund statement allocation jobs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Queues MIP <code>total_distributions</code> backfill (
            <code>fund_mip_totals</code>), then Transfer Fund allocation lines (
            <code>fund_transfer_allocations</code>) for annual statement PDFs.
            Leave as-of empty to process all MIP years. Requires the worker to
            be running.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              label="As-of date (optional)"
              type="date"
              size="small"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="YYYY-MM-DD; empty = all years"
              sx={{ minWidth: 220 }}
            />
            <Button
              variant="contained"
              onClick={handleQueueFundStatementJobs}
              disabled={queueing}
            >
              {queueing ? 'Queueing…' : 'Queue fund statement jobs'}
            </Button>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} sx={{ px: 2, pb: 2 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Backfill user Member IDs (imis_id)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Queues <code>backfill_user_imis_ids</code> to assign sequential
            Member IDs to users with a null or empty <code>imis_id</code>.
            Existing values are never overwritten. Requires the worker to be
            running.
          </Typography>
          <Button
            variant="contained"
            onClick={handleQueueBackfillUserImisIDs}
            disabled={queueingImisBackfill}
          >
            {queueingImisBackfill ? 'Queueing…' : 'Queue imis_id backfill'}
          </Button>
        </Paper>
      </Grid>

      <Grid item xs={12} sx={{ px: 2, pb: 2 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Backfill address geocodes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Queues <code>backfill_address_geocodes</code> (Primary living
            members with null <code>geocode_status</code>) and{' '}
            <code>backfill_chapter_geocodes</code> (chapter postal centroid or
            linked address). Requires the worker; map API key for Google
            fallback. Run once after deploy; not a cron.
          </Typography>
          <Button
            variant="contained"
            onClick={handleQueueBackfillAddressGeocodes}
            disabled={queueingGeocodeBackfill}
          >
            {queueingGeocodeBackfill
              ? 'Queueing…'
              : 'Queue address geocode backfill'}
          </Button>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box sx={{ mb: 3 }}>
              <AlertItem
                severity="error"
                description="Failed to load scheduled tasks. Please try refreshing the page."
                onClose={() => {}}
                keepOpen={true}
              />
            </Box>
          )}

          {!isLoading && !error && tasks && (
            <>
              {tasks.length === 0 ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ py: 4 }}
                >
                  No scheduled tasks found.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Name
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Description
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Cron Expression
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Status
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Last Run At
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Next Run At
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasks.map((task) => (
                        <TableRow key={task.id} hover>
                          <TableCell>
                            <Typography variant="body2">{task.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {task.description || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {task.cron_expression}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                task.is_active === 1 ? 'Active' : 'Inactive'
                              }
                              color={
                                task.is_active === 1 ? 'success' : 'default'
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateTime(task.last_run_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatNextRun(task.next_run_at)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ScheduledTasks;
